import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  NgbTypeaheadModule,
  NgbTypeaheadSelectItemEvent,
} from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  switchMap,
  throwError,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DfIconModule } from '@lib/fresco';

import { AuthService } from '@dg/shared-services';
import { DgError } from '@app/shared/models/dg-error';
import { TypeaheadSearchFunction } from '@app/shared/shared-api.model';

import { ContentResultComponent } from '@app/orgs/components/org-home/org-insights/org-links-form/content-search/content-result/content-result.component';
import { OrgEndorsedService } from '@app/orgs/services/org-endorsed.service';
import { scrollToElement } from '@app/orgs/components/org-home/utils/popover-helper';
import { SearchService } from '@app/search/services/search.service';
import { AutocompleteItem } from '@app/user-content/course-api.model';
import { RecommendingItem } from '@app/recommendations/recommendations.model';
import { createEnhancedRecommendingItem } from '@app/recommendations/recommendations.utils';

@Component({
    selector: 'dgx-assignment-content-search',
    imports: [
        NgbTypeaheadModule,
        ContentResultComponent,
        DfIconModule,
        NgIf,
        FormsModule,
        CommonModule,
    ],
    templateUrl: './assignment-content-search.component.html',
    styleUrl: './assignment-content-search.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssignmentContentSearchComponent implements OnInit {
  @Input() public selectedItem: RecommendingItem;
  @Output() public selectionChanged = new EventEmitter<RecommendingItem>();
  @Output() public uponBlurOfContentNameField = new EventEmitter<null>();
  @Output() public noContentResults = new EventEmitter<boolean>();

  public endorsedSrc = '';
  public isLoading: boolean = false;
  public inputText: string = '';

  public i18n = this.translate.instant([
    'Core_Optional',
    'recommendationForm_ContentName',
    'recommendationForm_SearchForContent',
    'Pathways_SearchError', // Generic search error; not specific to pathways
  ]);

  constructor(
    private translate: TranslateService,
    private authService: AuthService,
    private orgEndorsedService: OrgEndorsedService,
    private searchService: SearchService,
    private cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    const authUser = this.authService.authUser;
    if (authUser) {
      const defaultOrganization = authUser.defaultOrgInfo;
      this.endorsedSrc = this.orgEndorsedService.getEndorsedSrc(
        defaultOrganization?.organizationId
      );
    }
  }

  public onSearch: TypeaheadSearchFunction<string, any> = (
    searchTerm: Observable<string>
  ): Observable<readonly AutocompleteItem[]> => {
    this.isLoading = true;
    return this.loadContent(searchTerm).pipe(
      finalize(() => (this.isLoading = false))
    );
  };

  public onBlur(): void {
    this.inputText = '';
    this.uponBlurOfContentNameField.emit();
  }

  public onSelect(event: NgbTypeaheadSelectItemEvent): void {
    event?.preventDefault?.();
    this.inputText = '';
    const enhancedItem = createEnhancedRecommendingItem(
      event.item.model as RecommendingItem,
      this.authService.authUser
    );
    Object.keys(enhancedItem).forEach((key) => {
      this.selectedItem[key] = enhancedItem[key];
    });
    this.selectionChanged.emit(this.selectedItem);
    this.cdr.markForCheck();
  }

  public removeSelection() {
    this.inputText = '';
    this.selectedItem = {};
    this.selectionChanged.emit(this.selectedItem);
    this.cdr.markForCheck();
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Backspace' && this.inputText === '') {
      this.noContentResults.emit(false);
    }
    scrollToElement(event);
  }

  public loadContent = (
    searchTerm: Observable<string>
  ): Observable<readonly AutocompleteItem[]> =>
    searchTerm.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((searchTerm) => searchTerm.length >= 2),
      switchMap((searchTerm: string) => {
        this.noContentResults.emit(false);
        return this.searchService
          .search(
            searchTerm,
            [],
            10,
            0,
            this.authService.authUser.defaultOrgInfo.organizationId
          )
          .pipe(
            map((response) => {
              this.noContentResults.emit(response.results.length === 0);
              return response.results as unknown as AutocompleteItem[];
            })
          );
      }),
      catchError((error: Error) =>
        throwError(() => new DgError(this.i18n.Pathways_SearchError, error))
      )
    );
}
